import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import toast from 'react-hot-toast';

const Reports = () => {
  const [summary, setSummary] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => { fetchReportData(); }, []);

  const fetchReportData = async () => {
    try {
      const [summaryRes, expiringRes, reminderRes] = await Promise.all([
        reportsAPI.getSummary(), reportsAPI.getExpiring({ days: 30 }), reportsAPI.getReminderHistory()
      ]);
      setSummary(summaryRes.data);
      setExpiring(expiringRes.data.subscriptions || []);
      setReminderHistory(reminderRes.data);
    } catch (error) {
      toast.error('Gagal memuat data laporan');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await reportsAPI.exportCSV({ type: 'subscriptions' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscriptions_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Berhasil di-export');
    } catch (error) {
      toast.error('Gagal export');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Laporan</h1>
        <button onClick={handleExport} className="btn-primary text-sm">Export CSV</button>
      </div>

      <div className="flex gap-1 sm:gap-2 border-b overflow-x-auto">
        {[['summary', 'Ringkasan'], ['expiring', `Berakhir (${expiring.length})`], ['history', 'Riwayat']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-3 sm:px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === key ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && summary && (
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            <SummaryCard title="Total Langganan" value={summary.totalSubscriptions} color="text-primary-600" />
            <SummaryCard title="Total Pengeluaran" value={`Rp ${summary.totalExpenditure?.toLocaleString('id-ID') || 0}`} color="text-green-600" />
            <SummaryCard title="Tahun Ini" value={summary.purchasesThisYear} color="text-blue-600" />
          </div>

          <div className="card">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Status Langganan</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {summary.subscriptionsByStatus?.map((item) => (
                <div key={item.status} className="text-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <p className="text-xl sm:text-2xl font-bold">{item.count}</p>
                  <p className="text-xs sm:text-sm text-gray-500 capitalize">{item.status?.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </div>

          {summary.topDepartments?.length > 0 && (
            <div className="card">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Top Department</h3>
              <div className="space-y-2 sm:space-y-3">
                {summary.topDepartments.map((dept, i) => (
                  <div key={dept.department} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <span className="w-7 h-7 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                        <span className="text-xs sm:text-sm font-medium text-primary-600">{i + 1}</span>
                      </span>
                      <span className="font-medium text-sm sm:text-base">{dept.department}</span>
                    </div>
                    <span className="text-gray-600 text-xs sm:text-sm">{dept.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'expiring' && (
        <div className="card">
          {expiring.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">Tidak ada langganan berakhir dalam 30 hari</p>
          ) : (
            <>
              <div className="sm:hidden space-y-3">
                {expiring.map((sub) => {
                  const days = Math.ceil((new Date(sub.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={sub.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{sub.user?.name}</p>
                          <p className="text-xs text-gray-500">{sub.user?.department}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${days <= 7 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {days} hari
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">{sub.zoomAccountEmail}</p>
                      <p className="text-xs text-gray-400 mt-1">Berakhir: {new Date(sub.expiryDate).toLocaleDateString('id-ID')}</p>
                    </div>
                  );
                })}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Email Zoom</th>
                      <th className="px-4 py-3">Berakhir</th>
                      <th className="px-4 py-3">Sisa Hari</th>
                      <th className="px-4 py-3">Kontak</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {expiring.map((sub) => {
                      const days = Math.ceil((new Date(sub.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                      return (
                        <tr key={sub.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{sub.user?.name}</p>
                            <p className="text-sm text-gray-500">{sub.user?.department}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{sub.zoomAccountEmail}</td>
                          <td className="px-4 py-3 text-sm">{new Date(sub.expiryDate).toLocaleDateString('id-ID')}</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${days <= 7 ? 'text-red-600' : 'text-yellow-600'}`}>{days} hari</span>
                          </td>
                          <td className="px-4 py-3 text-sm">{sub.user?.phone || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          {reminderHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">Belum ada riwayat reminder</p>
          ) : (
            <>
              <div className="sm:hidden space-y-3">
                {reminderHistory.map((r) => (
                  <div key={r.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{r.subscription?.user?.name}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{r.subscription?.zoomAccountEmail}</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">{r.reminderType}</span>
                      <span>{r.sentDate ? new Date(r.sentDate).toLocaleString('id-ID') : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-4 py-3">Kirim</th>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Email Zoom</th>
                      <th className="px-4 py-3">Tipe</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reminderHistory.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{r.sentDate ? new Date(r.sentDate).toLocaleString('id-ID') : '-'}</td>
                        <td className="px-4 py-3">{r.subscription?.user?.name}</td>
                        <td className="px-4 py-3 text-sm">{r.subscription?.zoomAccountEmail}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{r.reminderType}</span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ title, value, color }) => (
  <div className="card">
    <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-4">{title}</h3>
    <p className={`text-2xl sm:text-4xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = { sent: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800' };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
};

export default Reports;
